import { RuleDecl } from 'vue/types/options'
import { required, email } from 'vuelidate/lib/validators'
import { FormFieldTypes, IFormField } from 'types/forms'
import {
  DEFAULT_MAX_UPLOAD_SIZE_MB,
  PROP_AMOUNT_ID,
  PROP_UNIT_ID,
  PROP_MULTIPLE_REQUIRED_ID,
  ALLOWED_EXTENSIONS,
  SEND_MAIL,
  CHECK_MAIL
} from './constants'

// just using overflow: hidden does not work for IOS therefor use preventDefault
export function disableScrolling(): void {
  document.querySelector('html')?.classList.add('no-scroll')
  document.ontouchmove = e => e.preventDefault()
}

export function roundOffAmount(salesUnit: number | string, amount: number| string): number {
  if (typeof salesUnit === 'string') salesUnit = +(salesUnit)
  if (typeof amount === 'string') amount = +(amount)
  if(salesUnit === 0) salesUnit = 1
  const modulo = amount % salesUnit

  return modulo === 0 ? amount : amount - modulo + salesUnit
}

export function onKeyDown(event: any): void {
  //To disable both letter e and minus sign -
  if(event.keyCode === 69 || event.keyCode === 189) {
    event.preventDefault()
  }
}

export function enableScrolling(): void {
  document.querySelector('html')?.classList.remove('no-scroll')
  document.ontouchmove = () => true
}

export function isInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

export function scrollToElement(el: Element, cssSelector: string): void {
  const firstElement = el.querySelector(cssSelector) as HTMLElement
  if (firstElement && !isInViewport(firstElement)) firstElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
  else if (!firstElement) console.warn(`The element with css selector: ${cssSelector} could not be found`)
}

export function getValidations(formFields: IFormField[]): { model: { [key in string]: RuleDecl } } {
  const modelValidations: { [key in string]: RuleDecl } = {};
  formFields.forEach(input => {
    modelValidations[input.model] = {}
    // if (input.required && input.type === FormFieldTypes.CheckboxList) modelValidations[input.model].required = (array: any[]) => array.length > 0
    if (input.required && input.type === FormFieldTypes.Checkbox) modelValidations[input.model].required = (val: boolean) => val
    else if (input.required) modelValidations[input.model].required = required
    if (input.type === FormFieldTypes.Email) modelValidations[input.model].email = email

    if (input.type === FormFieldTypes.File) {
      modelValidations[input.model].isValidFile = (val: File | null) => {
        return !val || (!input.allowedExtensions || input.allowedExtensions.includes(`.${val.name.split('.').pop()}` || val.name)) &&
          val.size / 1024 / 1024 < (input.maxFileSizeInMb || DEFAULT_MAX_UPLOAD_SIZE_MB)
      }
    }

  })

  return { model: modelValidations }
}


export function getGenericValidation(formFields: any): { model: { [key in string]: RuleDecl } } {
  const modelValidations: { [key in string]: RuleDecl } = {};
  formFields.forEach((input: any) => {
    modelValidations[input.labelForm] = {}
    if (input.requiredForm) modelValidations[input.labelForm].requiredForm = required
    if (input.validationForm === SEND_MAIL || input.validationForm === CHECK_MAIL) modelValidations[input.labelForm].validationForm = email

    if (input.type === 'File') {
      const allowedExtensions = ALLOWED_EXTENSIONS

      modelValidations[input.labelForm].isValidFile = (val: File | null) => {
        return !val || (!allowedExtensions || allowedExtensions.includes(`.${val.name.toLowerCase().split('.').pop()}` || val.name)) &&
          val.size / 1024 / 1024 < (DEFAULT_MAX_UPLOAD_SIZE_MB)
      }
    }

  })

  return { model: modelValidations }
}


export function setNecessaryFormFields(vueElement: Vue, formFields: IFormField[]): void {
  formFields.forEach(field => {
    if (field.type === FormFieldTypes.Checkbox) vueElement.$set((vueElement as any).model, field.model, false)
  })
}

export function setNecessaryGenericFormFields(vueElement: Vue, formFields: any): void {
  formFields.forEach((field: any) => {
    if (field.type === 'CheckBox') vueElement.$set((vueElement as any).model, field.model, false)
  })
}

export function isPropAmountOrUnit(id: string): boolean {
  return [PROP_UNIT_ID, PROP_AMOUNT_ID].includes(id)
}

export function queryParamUrl(url: string, param: string) {
  if (window.location.search.indexOf(`${param}=`) > -1) {
    return new URLSearchParams(url)?.get(param)?.toString()
  } else return
}

// Some API responses expose values on `value`, others on the first item in `values`.
function getPropertyValue(prop: any): any {
  if (!prop) return null
  if (prop.value !== undefined && prop.value !== null && prop.value !== '') return prop.value
  if (Array.isArray(prop.values) && prop.values.length > 0) {
    const first = prop.values[0]
    return typeof first === 'object' ? (first.value ?? first.name ?? first.displayName ?? null) : first
  }
  return null
}

// Cart items can expose either `properties` or `dynamicProperties`.
function normalizeProperties(properties: any): any[] {
  if (Array.isArray(properties)) return properties
  const list: any[] = []
  if (Array.isArray(properties?.properties)) list.push(...properties.properties)
  if (Array.isArray(properties?.dynamicProperties)) list.push(...properties.dynamicProperties)
  return list
}

export function findPropWithId(properties: any, id: string, fallback: string): string {
  const list = normalizeProperties(properties)
  const prop = list.find((property: any) => property?.id && property.id.includes(id))
  const value = getPropertyValue(prop)
  return value !== null && value !== undefined && value !== '' ? value : fallback
}

function parseMultipleRequiredValue(value: any, fallback = true): boolean {
  if (value === undefined || value === null || value === '') return fallback
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['true', 'ja', 'yes', 'oui', '1'].includes(normalized)) return true
    if (['false', 'nee', 'no', 'non', '0'].includes(normalized)) return false
  }
  return fallback
}

export function isSalesMultipleRequired(properties: any): boolean {
  // When the property is missing or unreadable, default to true to keep current behavior.
  const value = findPropWithId(properties, PROP_MULTIPLE_REQUIRED_ID, '')
  return parseMultipleRequiredValue(value, true)
}

export function findPropWithIdMultiplyValues(properties: any, id: string) {
  const prop = properties.find((property: any) => property.id && property.id === id)
  return prop ? prop.values : []
}

export function findPropWithIdForIcons(properties: any, id: string) {
  // Only searches for NL values, as we don't have the correct images for all languages
  const prop = properties.find((property: any) => property.id === id)
  return prop ? prop.localizedValues.filter((item: any) => item.language.cultureName === "nl-NL") : []
}
